(() => {
  if (customElements.get('ss-mix-picker')) return;
  customElements.define('ss-mix-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.variants = new Map(this.config.variants.map(v => [String(v.id), {
        ...v,
        thumbnail: v.thumbnail || this.resizedImage(v.image, 320),
        mobileImage: v.mobileImage || this.resizedImage(v.image, 800)
      }]));
      this.stage = this.closest('.ss-refined')?.querySelector('[data-design-stage]');
      this.productPhotos = this.closest('.ss-refined')?.querySelector('[data-product-photos]');
      this.designIds = Array.from(this.querySelectorAll('[data-design-card]'), card => card.dataset.designCard);
      this.selected = [];
      this.busy = false;
      this.storageKey = 'ss-mix-v1-' + this.dataset.product;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        if (Array.isArray(saved)) this.selected = saved.filter(id => this.variants.get(String(id))?.available).map(String);
      } catch (_) {}
      this.addEventListener('click', event => {
        const button = event.target.closest('button, a[data-design]');
        if (!button || !this.contains(button) || this.busy) return;
        if (button.hasAttribute('data-design-tab')) {
          this.activateGroup(button.dataset.designTab);
        } else if (button.hasAttribute('data-add')) {
          const id = button.dataset.add;
          if (this.variants.get(id)?.available) { this.selected.push(id); this.setStage(id); }
          this.update();

        } else if (button.hasAttribute('data-preview-add')) {
          const id = button.closest('[data-preview-panel]').dataset.currentPreview;
          if (this.variants.get(id)?.available) { this.selected.push(id); this.setStage(id); }
          this.update();
        } else if (button.hasAttribute('data-design')) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault();
          this.hideHover();
          this.setStage(button.dataset.design);
          if (!this.stage || matchMedia('(max-width: 1023px)').matches) this.openPreview(button.dataset.design);
          else this.syncPreview(button.dataset.design);
        } else if (button.hasAttribute('data-remove')) {
          const index = Number(button.dataset.remove);
          this.selected.splice(index, 1);
          if (!this.selected.length) this.showProductPhotos();
          else if (!this.selected.includes(this.stageId)) this.setStage(this.selected.at(-1));
          this.update();
          const remaining = this.querySelectorAll('[data-remove]');
          (remaining[Math.min(index, remaining.length - 1)] || this.querySelector('[data-add]'))?.focus();
        } else if (button.hasAttribute('data-clear')) {
          this.selected = [];
          this.showProductPhotos();
          this.update();
          this.querySelector('[data-add]')?.focus();
        } else if (button.hasAttribute('data-scroll')) {
          const rail = button.closest('.ss-mix-carousel').querySelector('.ss-mix-grid');
          rail.scrollBy({left: Number(button.dataset.scroll) * rail.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        } else if (button.hasAttribute('data-preview')) {
          this.openPreview(button.dataset.preview);
        } else if (button.hasAttribute('data-preview-step')) {
          this.stepPreview(button.closest('[data-preview-panel]'), Number(button.dataset.previewStep));
        } else if (button.hasAttribute('data-hover-close')) this.hideHover();
        else if (button.hasAttribute('data-close')) this.querySelector('dialog').close();
        else if (button.hasAttribute('data-submit')) this.submit();
      });
      this.imageObservers = [];
      this.querySelectorAll('.ss-mix-grid').forEach(rail => {
        if (!('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            this.loadDesignImage(entry.target);
            observer.unobserve(entry.target);
          });
        }, {root: rail, rootMargin: '0px 120px', threshold: 0.01});
        rail.querySelectorAll('[data-design-image]').forEach(node => observer.observe(node));
        this.imageObservers.push(observer);
      });
      this.groupTabs = [...this.querySelectorAll('[data-design-tab]')].filter(tab => {
        const panel = this.querySelector('[data-design-group="' + tab.dataset.designTab + '"]');
        tab.hidden = !panel?.querySelector('[data-design-card]');
        return !tab.hidden;
      });
      if (this.groupTabs.length) {
        this.querySelector('[role="tablist"]').hidden = false;
        const navGroup = new URLSearchParams(location.hash.slice(1)).get('skin-style');
        const initialGroup = ['solids', 'textures', 'other'].includes(navGroup) && !this.dataset.linkedDesign ? navGroup : this.dataset.initialGroup;
        this.activateGroup(this.groupTabs.find(tab => tab.dataset.designTab === (initialGroup || 'other'))?.dataset.designTab || this.groupTabs[0].dataset.designTab);
        this.querySelector('[role="tablist"]').addEventListener('keydown', event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          const index = this.groupTabs.indexOf(event.target);
          if (index < 0) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.groupTabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.groupTabs.length) % this.groupTabs.length;
          this.activateGroup(this.groupTabs[next].dataset.designTab);
          this.groupTabs[next].focus();
        });
      }
      const rails = this.querySelectorAll('.ss-mix-grid');
      rails.forEach(rail => rail.addEventListener('scroll', () => { this.updateArrows(); this.hideHover(); }, {passive:true}));
      this.querySelectorAll('[data-design-card]').forEach(card => {
        card.addEventListener('pointerenter', event => {
          if (!this.closest('.ss-refined') && event.pointerType === 'mouse' && matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)').matches) this.showHover(card);
        });
        card.addEventListener('pointerleave', () => this.scheduleHoverClose());
      });
      const hover = this.querySelector('.ss-mix-hover');
      hover.addEventListener('pointerenter', () => clearTimeout(this.hoverTimer));
      hover.addEventListener('pointerleave', () => this.scheduleHoverClose());
      this.hoverAbort = new AbortController();
      document.addEventListener('pointerdown', event => {
        if (!hover.contains(event.target) && !event.target.closest('[data-design-card]')) this.hideHover();
      }, {capture:true, signal:this.hoverAbort.signal});
      window.addEventListener('scroll', () => this.hideHover(), {passive:true, capture:true, signal:this.hoverAbort.signal});
      window.addEventListener('resize', () => this.hideHover(), {passive:true, signal:this.hoverAbort.signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.hideHover();
        const panel = event.target.closest('[data-preview-panel]');
        if (panel && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          this.stepPreview(panel, event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      rails.forEach(rail => this.resizeObserver.observe(rail));
      const dialog = this.querySelector('dialog');
      const previewImage = dialog.querySelector('img');
      previewImage.draggable = false;
      let swipe = null;
      dialog.addEventListener('pointerdown', event => {
        if (!event.isPrimary) { swipe = null; return; }
        if (event.target !== previewImage || event.button !== 0) return;
        swipe = {id:event.pointerId, x:event.clientX, y:event.clientY};
        previewImage.setPointerCapture(event.pointerId);
      });
      previewImage.addEventListener('pointerup', event => {
        if (!swipe || swipe.id !== event.pointerId) return;
        const dx = event.clientX - swipe.x, dy = event.clientY - swipe.y;
        swipe = null;
        if (Math.abs(dx) >= 36 && Math.abs(dx) > Math.abs(dy) * 1.25) {
          this.stepPreview(dialog, dx < 0 ? 1 : -1);
        }
      });
      previewImage.addEventListener('pointercancel', () => { swipe = null; });
      previewImage.addEventListener('lostpointercapture', () => { swipe = null; });
      dialog.addEventListener('close', () => { swipe = null; });
      dialog.addEventListener('close', () => this.syncPreview(dialog.dataset.currentPreview, true));
      const outside = event => {
        const bounds = dialog.getBoundingClientRect();
        return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
      };
      let startedOutside = false;
      dialog.addEventListener('pointerdown', event => { startedOutside = event.target === dialog && outside(event); });
      dialog.addEventListener('click', event => {
        if (event.target === dialog && startedOutside && outside(event)) dialog.close();
        startedOutside = false;
      });
      if (this.stage) {
        this.stage.addEventListener('click', event => {
          const choice = event.target.closest('[data-stage-choice]');
          if (event.target.closest('[data-product-photos-back]')) this.showProductPhotos(true);
          else if (choice) this.setStage(choice.dataset.stageChoice);
          else if (event.target.closest('[data-stage-zoom]')) this.openPreview(this.stageId);
        }, {signal:this.hoverAbort.signal});
        const linkedDesign = this.variants.has(this.dataset.linkedDesign) ? this.dataset.linkedDesign : null;
        this.setStage(linkedDesign || this.selected.at(-1) || this.stage.dataset.initialDesign, Boolean(linkedDesign));
        if (linkedDesign) this.syncPreview(linkedDesign);
      }
      this.selectionObserver = new IntersectionObserver(entries => {
        document.body.classList.toggle('ss-selecting-skins', entries.some(entry => entry.isIntersecting));
      });
      this.selectionObserver.observe(this);
      this.dataset.ready = 'true';
      this.updateArrows();
      this.update();
    }
    designLabel(id) {
      return (this.config.text.designLabel || '__DESIGN__').replace('__DESIGN__', this.variants.get(id)?.title || '');
    }
    showProductPhotos(restoreFocus = false) {
      if (!this.stage || !this.productPhotos) return;
      this.stage.hidden = true;
      this.productPhotos.hidden = false;
      if (restoreFocus) this.productPhotos.focus({preventScroll:true});
    }
    setStage(id, reveal = true) {
      const variant = this.variants.get(id);
      if (!variant || !this.stage) return;
      this.stageId = id;
      if (reveal && this.productPhotos) {
        this.stage.hidden = false;
        this.productPhotos.hidden = true;
      }
      const image = this.stage.querySelector('img');
      if (reveal) {
        image.removeAttribute('srcset');
        image.src = this.previewSource(variant);
        image.loading = 'eager';
      }
      image.alt = variant.alt || this.designLabel(id);
      this.stage.querySelector('[data-stage-label]').textContent = this.designLabel(id);
      this.stage.querySelector('[data-stage-zoom]').setAttribute('aria-label', this.config.text.preview + ' ' + this.designLabel(id));
      this.stage.querySelectorAll('[data-stage-choice]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.stageChoice === id)));
      this.querySelectorAll('[data-design-card]').forEach(card => card.classList.toggle('is-previewed', card.dataset.designCard === id));
    }
    updateStageChoices() {
      if (!this.stage) return;
      const rail = this.stage.querySelector('[data-stage-choices]');
      rail.replaceChildren();
      const ids = [...new Set(this.selected)];
      rail.hidden = ids.length === 0;
      ids.forEach(id => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.stageChoice = id;
        button.setAttribute('aria-label', this.config.text.preview + ' ' + this.designLabel(id));
        button.setAttribute('aria-pressed', String(this.stageId === id));
        const image = document.createElement('img');
        image.src = this.thumbnailSource(id);
        image.decoding = 'async';
        image.alt = '';
        image.width = 56;
        image.height = 56;
        const label = document.createElement('span');
        label.textContent = this.designLabel(id);
        button.append(image, label);
        rail.append(button);
      });
    }
    resizedImage(source, width) {
      if (!source) return '';
      try {
        const url = new URL(source, location.origin);
        url.searchParams.set('width', String(width));
        return url.href;
      } catch (_) { return source; }
    }
    previewSource(variant) {
      return matchMedia('(max-width: 1023px)').matches ? variant.mobileImage || variant.image : variant.image;
    }
    thumbnailSource(id) {
      const variant = this.variants.get(id);
      const image = this.querySelector('[data-design-card="' + id + '"] .ss-mix-art img');
      return image?.currentSrc || variant.thumbnail || variant.image;
    }
    loadDesignImage(placeholder) {
      const variant = this.variants.get(placeholder.dataset.designImage);
      if (!variant || !placeholder.isConnected) return;
      const image = document.createElement('img');
      image.width = 320;
      image.height = 320;
      image.alt = variant.alt || variant.title;
      image.decoding = 'async';
      image.src = variant.thumbnail || variant.image;
      placeholder.replaceWith(image);
    }
    activateGroup(group) {
      this.groupTabs.forEach(tab => {
        const active = tab.dataset.designTab === group;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = this.querySelector('[data-design-group="' + tab.dataset.designTab + '"]');
        panel.hidden = !active;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
      });
      const activePanel = this.querySelector('[data-design-group="' + group + '"]');
      // Reveal the first row immediately; observe the remaining cards as they enter the rail.
      const cards = [...(activePanel?.querySelectorAll('[data-design-card]') || [])];
      cards.slice(0, 'IntersectionObserver' in window ? 4 : cards.length).forEach(card => {
        const placeholder = card.querySelector('[data-design-image]');
        if (placeholder) this.loadDesignImage(placeholder);
      });
      this.activeGroup = group;
      this.hideHover();
      requestAnimationFrame(() => this.updateArrows());
    }
    disconnectedCallback() {
      this.imageObservers?.forEach(observer => observer.disconnect());
      this.resizeObserver?.disconnect();
      this.selectionObserver?.disconnect();
      document.body.classList.remove('ss-selecting-skins');
      this.hoverAbort?.abort();
      this.hideHover();
    }
    updateArrows() {
      this.querySelectorAll('.ss-mix-carousel').forEach(carousel => {
        const rail = carousel.querySelector('.ss-mix-grid');
        carousel.querySelector('[data-scroll="-1"]').disabled = rail.scrollLeft <= 1;
        carousel.querySelector('[data-scroll="1"]').disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
      });
    }
    scheduleHoverClose() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => this.hideHover(), 220);
    }
    hideHover() {
      clearTimeout(this.hoverTimer);
      const preview = this.querySelector('.ss-mix-hover');
      if (preview?.matches(':popover-open')) {
        preview.hidePopover();
        if (preview.dataset.navigated === 'true') this.syncPreview(preview.dataset.currentPreview);
      }
    }
    showHover(card) {
      if (this.busy || this.querySelector('dialog').open) return;
      clearTimeout(this.hoverTimer);
      const preview = this.querySelector('.ss-mix-hover');
      const rect = card.getBoundingClientRect();
      const width = Math.min(280, innerWidth - 24);
      const height = width + 116;
      let left = rect.left + (rect.width - width) / 2;
      let top = rect.top - height - 12;
      if (top < 12) {
        left = rect.left >= width + 24 ? rect.left - width - 12 : rect.right + 12;
        top = Math.min(rect.top, innerHeight - height - 12);
      }
      preview.style.width = width + 'px';
      preview.style.left = Math.max(12, Math.min(left, innerWidth - width - 12)) + 'px';
      preview.style.top = Math.max(12, top) + 'px';
      preview.dataset.navigated = 'false';
      this.renderPreview(preview, card.dataset.designCard);
      if (!preview.matches(':popover-open')) preview.showPopover();
    }
    renderPreview(panel, id) {
      const variant = this.variants.get(id);
      if (!variant) return;
      this.setStage(id);
      panel.dataset.currentPreview = id;
      panel.querySelector('img').src = this.previewSource(variant);
      panel.querySelector('img').decoding = 'async';
      panel.querySelector('img').alt = variant.alt || this.designLabel(id);
      panel.querySelector('p').textContent = this.designLabel(id);
      const index = this.designIds.indexOf(id);
      panel.querySelector('[data-preview-step="-1"]').disabled = index === 0;
      panel.querySelector('[data-preview-step="1"]').disabled = index === this.designIds.length - 1;
      panel.querySelector('[data-preview-position]').textContent = (index + 1) + ' / ' + this.designIds.length;
      this.updatePreviewAdd(panel);
    }
    updatePreviewAdd(panel) {
      const id = panel.dataset.currentPreview;
      const button = panel.querySelector('[data-preview-add]');
      button.disabled = this.busy || !this.variants.get(id)?.available;
      const label = this.querySelector('[data-add="' + id + '"]')?.getAttribute('aria-label');
      if (label) button.setAttribute('aria-label', label);
      const count = this.selected.filter(selected => selected === id).length;
      const badge = panel.querySelector('[data-preview-count]');
      badge.textContent = count ? '×' + count : '';
      badge.hidden = count === 0;
    }
    stepPreview(panel, direction) {
      const index = this.designIds.indexOf(panel.dataset.currentPreview);
      const id = this.designIds[index + direction];
      if (!id) return;
      panel.dataset.navigated = 'true';
      this.renderPreview(panel, id);
    }
    syncPreview(id, restoreFocus = false) {
      const card = this.querySelector('[data-design-card="' + id + '"]');
      if (!card || !this.isConnected) return;
      this.querySelectorAll('[data-design-card]').forEach(item => item.classList.toggle('is-previewed', item === card));
      const group = card.closest('[data-design-group]')?.dataset.designGroup;
      if (group && this.groupTabs?.length) this.activateGroup(group);
      const rail = card.closest('.ss-mix-grid');
      const railRect = rail.getBoundingClientRect(), cardRect = card.getBoundingClientRect();
      if (restoreFocus) card.querySelector('[data-design]').focus({preventScroll:true});
      rail.scrollTo({left:rail.scrollLeft + cardRect.left - railRect.left - (rail.clientWidth - cardRect.width) / 2, behavior:'instant'});
      this.updateArrows();
    }
    openPreview(id) {
      if (!this.variants.has(id)) return;
      this.hideHover();
      const dialog = this.querySelector('dialog');
      this.renderPreview(dialog, id);
      if (!dialog.open) dialog.showModal();
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:this.dataset.currency || 'USD'}).format(cents / 100);
    }
    update() {
      const text = this.config.text;
      const counts = new Map();
      this.selected.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
      this.querySelectorAll('[data-add]').forEach(button => {
        const count = counts.get(button.dataset.add) || 0;
        const card = button.closest('[data-design-card]');
        card.classList.toggle('is-selected', count > 0);
        const badge = card.querySelector('.ss-mix-badge');
        badge.hidden = !count;
        badge.textContent = '×' + count;
      });
      const list = this.querySelector('[data-selected]');
      list.replaceChildren();
      this.selected.forEach((id, index) => {
        const variant = this.variants.get(id);
        const li = document.createElement('li');
        const preview = document.createElement('button');
        preview.type = 'button';
        preview.className = 'ss-mix-preview';
        preview.dataset.preview = id;
        preview.setAttribute('aria-label', text.preview + ' ' + variant.title);
        const image = document.createElement('img');
        image.src = this.thumbnailSource(id);
        image.decoding = 'async';
        image.alt = variant.alt || this.designLabel(id);
        image.width = 160;
        image.height = 160;
        preview.append(image);
        const label = document.createElement('span');
        label.className = 'ss-mix-style';
        label.textContent = this.designLabel(id);
        preview.append(label);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ss-mix-remove';
        remove.dataset.remove = index;
        remove.textContent = '×';
        remove.setAttribute('aria-label', text.remove + ' ' + variant.title + ' (' + (index + 1) + ')');
        li.append(preview, remove);
        list.append(li);
      });
      this.updateStageChoices();
      const count = this.selected.length;
      const total = this.selected.reduce((sum, id) => sum + this.variants.get(id).price, 0);
      this.querySelector('[data-count]').textContent = String(count);
      this.querySelector('[data-empty]').hidden = count > 0;
      this.querySelector('[data-clear]').hidden = count === 0;
      this.querySelector('[data-total]').textContent = this.money(total);
      const productInfo = this.closest('product-info');
      const topPrice = productInfo?.querySelector('[data-refined-total]');
      const topLabel = productInfo?.querySelector('[data-refined-price-label]');
      if (topPrice && topLabel) {
        if (!this.initialTopPrice) this.initialTopPrice = [topPrice.textContent, topLabel.textContent];
        topPrice.textContent = count >= 2 ? this.money(total) : this.initialTopPrice[0];
        topLabel.textContent = count >= 2 ? text.total : this.initialTopPrice[1];
      }
      const status = count === 0 ? text.chooseTwo : count === 1 ? text.chooseOne : text.ready;
      this.querySelector('[data-status]').textContent = status;
      const submit = this.querySelector('[data-submit]');
      submit.disabled = count < 2 || this.busy;
      submit.textContent = this.busy ? text.adding : count < 2 ? status : text.add + ' · ' + this.money(total);
      this.querySelectorAll('[data-add],[data-clear],[data-remove]').forEach(button => {
        button.disabled = this.busy || (button.hasAttribute('data-add') && !this.variants.get(button.dataset.add)?.available);
      });
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.selected)); } catch (_) {}
      this.querySelectorAll('[data-preview-panel]').forEach(panel => this.updatePreviewAdd(panel));
    }
    async submit() {
      if (this.busy || this.selected.length < 2) return;
      const error = this.querySelector('[data-error]');
      error.hidden = true;
      if (this.dataset.live !== 'true') {
        error.textContent = this.config.text.previewOnly;
        error.hidden = false;
        return;
      }
      this.busy = true;
      this.update();
      const counts = new Map();
      this.selected.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
      const items = Array.from(counts, ([id, quantity]) => ({id:Number(id),quantity,properties:{'Purchase unit':'1 skin','_ss_mix':'single-v1'}}));
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.config.text.error);
        this.selected = [];
        try {sessionStorage.removeItem(this.storageKey);} catch (_) {}
        window.location.assign(root + 'cart');
      } catch (exception) {
        error.textContent = exception.message || this.config.text.error;
        error.hidden = false;
        this.busy = false;
        this.update();
      }
    }
  });
})();







